import { useCallback, useRef, useState } from "react";
import {
    SR_VOICE_DEFAULT_MIME,
    SR_VOICE_RECORDER_TIMESLICE_MS,
} from "../sr-constants";
import { pickVoiceMime, stopMediaTracks } from "./sr-voice-utils";

export const useSrVoiceRecorder = () => {
    const [isRecording, setIsRecording] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mediaRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);

    const cleanup = () => {
        stopMediaTracks(streamRef.current);
        streamRef.current = null;
        mediaRef.current = null;
    };

    const start = useCallback(async () => {
        setError(null);
        if (typeof MediaRecorder === "undefined") {
            setError("Voice recording is not supported in this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            streamRef.current = stream;
            const mime = pickVoiceMime();
            const recorder = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data?.size) chunksRef.current.push(e.data);
            };
            mediaRef.current = recorder;
            recorder.start(SR_VOICE_RECORDER_TIMESLICE_MS);
            setIsRecording(true);
        } catch {
            cleanup();
            setError("Microphone permission denied or unavailable.");
            setIsRecording(false);
        }
    }, []);

    const stop = useCallback((): Promise<{ blob: Blob; mime: string } | null> => {
        return new Promise((resolve) => {
            const recorder = mediaRef.current;
            if (!recorder || recorder.state === "inactive") {
                cleanup();
                setIsRecording(false);
                resolve(null);
                return;
            }
            recorder.onstop = () => {
                const mime =
                    recorder.mimeType?.split(";")[0] || SR_VOICE_DEFAULT_MIME;
                const blob = new Blob(chunksRef.current, { type: mime });
                chunksRef.current = [];
                cleanup();
                setIsRecording(false);
                resolve(blob.size ? { blob, mime } : null);
            };
            recorder.stop();
        });
    }, []);

    const cancel = useCallback(() => {
        const recorder = mediaRef.current;
        if (recorder && recorder.state !== "inactive") {
            recorder.onstop = null;
            try {
                recorder.stop();
            } catch {
                /* ignore */
            }
        }
        chunksRef.current = [];
        cleanup();
        setIsRecording(false);
        setError(null);
    }, []);

    return { isRecording, error, start, stop, cancel };
};
